/**
 * Компонент QR-авторизации для Telegram Client
 * Дата: 30.12.2025
 * Назначение: Отображение QR-кода для авторизации Telegram аккаунта
 * Обновлено: 30.12.2025 - Используется qrcode.react для локальной генерации QR вместо внешнего API
 */
import { useEffect, useState } from 'react';
import { Button, Card, CardHeader, CardTitle, CardContent, Spinner } from '@/components/ui';
import { startQRAuth, checkAuthStatus, type QRAuthResult, type AuthStatus } from '@/api/telegramClient';
import { QRCodeSVG } from 'qrcode.react';
import { X } from 'lucide-react';

interface TelegramQRAuthProps {
  onSuccess: (status: AuthStatus) => void;
  onCancel: () => void;
  tenantId?: string; // Для SuperAdmin: передача tenantId для запросов
}

export function TelegramQRAuth({ onSuccess, onCancel, tenantId }: TelegramQRAuthProps) {
  const [qrData, setQrData] = useState<QRAuthResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  // Запустить QR-авторизацию
  useEffect(() => {
    let mounted = true;

    async function initQRAuth() {
      // Не делать запрос если tenantId не указан
      if (!tenantId) {
        setError('Выберите клинику для QR-авторизации');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const result = await startQRAuth(tenantId);
        if (mounted) {
          setQrData(result);
          setLoading(false);
          // Начать проверку статуса
          startStatusCheck();
        }
      } catch (err: any) {
        if (mounted) {
          setError(err.response?.data?.error || 'Не удалось запустить QR-авторизацию');
          setLoading(false);
        }
      }
    }

    initQRAuth();

    return () => {
      mounted = false;
    };
  }, [tenantId]);

  // Проверка статуса авторизации каждые 3 секунды
  const startStatusCheck = () => {
    const interval = setInterval(async () => {
      try {
        setChecking(true);
        const status = await checkAuthStatus(tenantId);
        if (status.status === 'connected') {
          clearInterval(interval);
          onSuccess(status);
        } else if (status.status === '2fa_required') {
          clearInterval(interval);
          onSuccess(status);
        }
      } catch (err) {
        console.error('Failed to check auth status:', err);
      } finally {
        setChecking(false);
      }
    }, 3000);

    // Остановить проверку через 5 минут (время жизни QR токена)
    setTimeout(() => {
      clearInterval(interval);
    }, 5 * 60 * 1000);
  };

  // Генерация QR-кода локально через qrcode.react
  const renderQRCode = () => {
    if (!qrData?.qr_code_url) return null;

    return (
      <div className="flex flex-col items-center gap-4">
        <div className="border-2 border-neutral-200 rounded-lg p-4 bg-white">
          <QRCodeSVG
            value={qrData.qr_code_url}
            size={256}
            level="H"
            includeMargin={true}
          />
        </div>
        <p className="text-sm text-neutral-600 text-center max-w-md">
          Откройте Telegram на телефоне → Настройки → Устройства → Связать устройство → Отсканируйте QR-код
        </p>
        {checking && (
          <div className="flex items-center gap-2 text-sm text-primary-600">
            <Spinner size="sm" />
            <span>Ожидание подтверждения...</span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ошибка подключения</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-error mb-4">{error}</p>
          <Button onClick={onCancel} variant="secondary">
            Закрыть
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Подключение Telegram аккаунта</CardTitle>
        <button
          onClick={onCancel}
          className="text-neutral-400 hover:text-neutral-600 transition-colors"
          aria-label="Закрыть"
        >
          <X className="w-5 h-5" />
        </button>
      </CardHeader>
      <CardContent>
        {renderQRCode()}
        {qrData && (
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <p className="text-xs text-neutral-500 text-center">
              QR-код действителен до: {new Date(qrData.expires_at).toLocaleString('ru-RU')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

